import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { 
  Save, 
  ArrowLeft,
  User,
  Building2,
  MapPin,
  CreditCard,
  Phone,
  FileText,
  GraduationCap,
  Briefcase,
  RefreshCw,
  Edit,
  UserCheck,
  UserX,
  Clock,
  Users,
  DollarSign,
  Plus,
  UserPlus,
  Copy,
  KeyRound
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkflowActivity from '../../../components/workflow/WorkflowActivity';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';

const EmployeeView = () => {
  const navigate = useNavigate();
  const { employee_id } = useParams();
  const { permissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [portalCreds, setPortalCreds] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [companyCodes, setCompanyCodes] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [personnelAreas, setPersonnelAreas] = useState([]);
  const [personnelSubareas, setPersonnelSubareas] = useState([]);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [employeeSubgroups, setEmployeeSubgroups] = useState([]);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState(null);

  const loadEmployee = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${backendUrl}/api/hcm/employees/${employee_id}`);
      setFormData(res.data);
    } catch (error) {
      console.error('Error loading employee:', error);
      toast.error('Failed to load employee');
      navigate('/hcm/employees');
    } finally {
      setLoading(false);
    }
  }, [employee_id, navigate, backendUrl]);

  const loadMasterData = useCallback(async () => {
    try {
      const [companyRes, ccRes, paRes, psaRes, egRes, esgRes] = await Promise.all([
        axios.get(`${backendUrl}/api/company/company-codes`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/controlling/cost-centers`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/hcm/personnel-areas`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/hcm/personnel-subareas`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/hcm/employee-groups`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/hcm/employee-subgroups`).catch(() => ({ data: [] }))
      ]);
      setCompanyCodes(companyRes.data);
      setCostCenters(ccRes.data);
      setPersonnelAreas(paRes.data);
      setPersonnelSubareas(psaRes.data);
      setEmployeeGroups(egRes.data);
      setEmployeeSubgroups(esgRes.data);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  }, [backendUrl]);

  useEffect(() => {
    loadEmployee();
    loadMasterData();
  }, [loadEmployee, loadMasterData]);

  const handlePersonalChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      personal_data: { ...prev.personal_data, [field]: value }
    }));
  };

  const handleOrgChange = (field, value) => {
    setFormData(prev => {
      const newOrgAssignment = { ...prev.organizational_assignment, [field]: value };
      
      // Cascade clear: if Personnel Area changes, clear Personnel Subarea
      if (field === 'personnel_area') {
        newOrgAssignment.personnel_subarea = '';
      }
      
      // Cascade clear: if Employee Group changes, clear Employee Subgroup
      if (field === 'employee_group') {
        newOrgAssignment.employee_subgroup = '';
      }
      
      return {
        ...prev,
        organizational_assignment: newOrgAssignment
      };
    });
  };

  const handleEmploymentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      employment_info: { ...prev.employment_info, [field]: value }
    }));
  };

  const handleCommChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      communication: { ...prev.communication, [field]: value }
    }));
  };

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => {
      const newAddresses = [...(prev.addresses || [])];
      if (!newAddresses[index]) newAddresses[index] = {};
      newAddresses[index] = { ...newAddresses[index], [field]: value };
      return { ...prev, addresses: newAddresses };
    });
  };

  const handleBankChange = (index, field, value) => {
    setFormData(prev => {
      const newBanks = [...(prev.bank_details || [])];
      if (!newBanks[index]) newBanks[index] = {};
      newBanks[index] = { ...newBanks[index], [field]: value };
      return { ...prev, bank_details: newBanks };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axios.put(`${backendUrl}/api/hcm/employees/${employee_id}`, {
        personal_data: formData.personal_data,
        organizational_assignment: formData.organizational_assignment,
        employment_info: formData.employment_info,
        communication: formData.communication,
        addresses: formData.addresses,
        bank_details: formData.bank_details,
        status: formData.status,
        remarks: formData.remarks
      });
      toast.success(res.data.message);
      setIsEditMode(false);
      loadEmployee();
    } catch (error) {
      console.error('Error updating employee:', error);
      // Handle Pydantic validation errors (array of {loc, msg, type, ctx} objects)
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to update employee');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleProvisionPortalUser = async () => {
    try {
      setProvisioning(true);
      const res = await axios.post(
        `${backendUrl}/api/rbac/users/provision-portal/${employee_id}`,
        {}
      );
      setPortalCreds(res.data);
      toast.success(res.data.message || 'Portal user created');
    } catch (error) {
      console.error('Error provisioning portal user:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to provision portal user');
    } finally {
      setProvisioning(false);
    }
  };

  const copyCredentials = () => {
    if (!portalCreds) return;
    const text = `Username: ${portalCreds.username}\nTemporary Password: ${portalCreds.temporary_password}`;
    navigator.clipboard?.writeText(text);
    toast.success('Credentials copied to clipboard');
  };


  const getStatusBadge = (status) => {
    const statusConfig = {
      'ACTIVE': { color: 'bg-green-100 text-green-700', icon: UserCheck },
      'INACTIVE': { color: 'bg-gray-100 text-gray-600', icon: UserX },
      'ON_LEAVE': { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'TERMINATED': { color: 'bg-red-100 text-red-700', icon: UserX },
      'RETIRED': { color: 'bg-orange-100 text-orange-700', icon: User }
    };
    const config = statusConfig[status] || statusConfig['ACTIVE'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="w-4 h-4" />
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  if (!formData) {
    return (
      <Layout>
        <div className="p-6">
          <Card>
            <CardContent className="p-8 text-center">
              <h3 className="text-lg font-medium text-gray-900">Employee not found</h3>
              <Button className="mt-4" onClick={() => navigate('/hcm/employees')}>
                Back to List
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const personal = formData.personal_data || {};
  const org = formData.organizational_assignment || {};
  const emp = formData.employment_info || {};
  const comm = formData.communication || {};
  const address = formData.addresses?.[0] || {};
  const bank = formData.bank_details?.[0] || {};

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="employee-view-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/hcm/employees')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">
                  {personal.first_name} {personal.middle_name || ''} {personal.last_name}
                </h1>
                {getStatusBadge(formData.status)}
              </div>
              <p className="text-gray-500">{formData.employee_id} • {org.position || 'No Position'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isEditMode ? (
              <>
                <Button variant="outline" onClick={() => { setIsEditMode(false); loadEmployee(); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-stone-800 hover:bg-stone-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <>
                {permissions?.is_superuser && (
                  <Button
                    variant="outline"
                    onClick={handleProvisionPortalUser}
                    disabled={provisioning}
                    data-testid="provision-portal-user-button"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {provisioning ? 'Creating...' : 'Provision Portal User'}
                  </Button>
                )}
                <Button
                  onClick={() => setIsEditMode(true)}
                  className="bg-stone-800 hover:bg-stone-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Employee
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Portal user credentials dialog */}
        <Dialog open={!!portalCreds} onOpenChange={(open) => { if (!open) setPortalCreds(null); }}>
          <DialogContent data-testid="portal-creds-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-orange-600" />
                Portal User Created
              </DialogTitle>
              <DialogDescription>
                Share these one-time credentials with the employee. The temporary
                password is shown only once.
              </DialogDescription>
            </DialogHeader>
            {portalCreds && (
              <div className="space-y-3">
                <div className="rounded-md border bg-stone-50 p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Username</span>
                    <span className="font-mono font-semibold" data-testid="portal-creds-username">{portalCreds.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Temporary Password</span>
                    <span className="font-mono font-semibold" data-testid="portal-creds-password">{portalCreds.temporary_password}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Linked Employee</span>
                    <span className="font-mono">{portalCreds.employee_id}</span>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={copyCredentials} data-testid="portal-creds-copy">
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
              <Button className="bg-stone-800 hover:bg-stone-700" onClick={() => setPortalCreds(null)} data-testid="portal-creds-close">
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Form Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="mb-4 overflow-x-auto">
            <TabsList className="inline-flex min-w-full">
              <TabsTrigger value="personal" className="flex items-center gap-1 text-xs px-3">
                <User className="w-3 h-3" /> Personal
              </TabsTrigger>
              <TabsTrigger value="organization" className="flex items-center gap-1 text-xs px-3">
                <Building2 className="w-3 h-3" /> Organization
              </TabsTrigger>
              <TabsTrigger value="employment" className="flex items-center gap-1 text-xs px-3">
                <Briefcase className="w-3 h-3" /> Employment
              </TabsTrigger>
              <TabsTrigger value="communication" className="flex items-center gap-1 text-xs px-3">
                <Phone className="w-3 h-3" /> Contact
              </TabsTrigger>
              <TabsTrigger value="address" className="flex items-center gap-1 text-xs px-3">
                <MapPin className="w-3 h-3" /> Address
              </TabsTrigger>
              <TabsTrigger value="bank" className="flex items-center gap-1 text-xs px-3">
                <CreditCard className="w-3 h-3" /> Bank
              </TabsTrigger>
              <TabsTrigger value="family" className="flex items-center gap-1 text-xs px-3">
                <Users className="w-3 h-3" /> Family
              </TabsTrigger>
              <TabsTrigger value="recurring" className="flex items-center gap-1 text-xs px-3">
                <DollarSign className="w-3 h-3" /> Recurring
              </TabsTrigger>
              <TabsTrigger value="additional" className="flex items-center gap-1 text-xs px-3">
                <Plus className="w-3 h-3" /> Additional
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Personal Data Tab */}
          <TabsContent value="personal">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-stone-700" />
                  Personal Information (Infotype 0002)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={personal.first_name || ''}
                      onChange={(e) => handlePersonalChange('first_name', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Middle Name</Label>
                    <Input
                      value={personal.middle_name || ''}
                      onChange={(e) => handlePersonalChange('middle_name', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={personal.last_name || ''}
                      onChange={(e) => handlePersonalChange('last_name', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input
                      type="date"
                      value={personal.date_of_birth || ''}
                      onChange={(e) => handlePersonalChange('date_of_birth', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <select
                      value={personal.gender || ''}
                      onChange={(e) => handlePersonalChange('gender', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="O">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label>Marital Status</Label>
                    <select
                      value={personal.marital_status || ''}
                      onChange={(e) => handlePersonalChange('marital_status', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="S">Single</option>
                      <option value="M">Married</option>
                      <option value="D">Divorced</option>
                      <option value="W">Widowed</option>
                    </select>
                  </div>
                  <div>
                    <Label>Nationality</Label>
                    <Input
                      value={personal.nationality || ''}
                      onChange={(e) => handlePersonalChange('nationality', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Birth Place</Label>
                    <Input
                      value={personal.birth_place || ''}
                      onChange={(e) => handlePersonalChange('birth_place', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Blood Group</Label>
                    <select
                      value={personal.blood_group || ''}
                      onChange={(e) => handlePersonalChange('blood_group', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="">Select</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organization Tab */}
          <TabsContent value="organization">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-stone-700" />
                  Organizational Assignment (Infotype 0001)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Company Code</Label>
                    <select
                      value={org.company_code || ''}
                      onChange={(e) => handleOrgChange('company_code', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="">Select Company</option>
                      {companyCodes.map(cc => (
                        <option key={cc.company_code_id} value={cc.company_code_id}>
                          {cc.company_code_id} - {cc.company_code_name}
                        </option>
                      ))}
                      {org.company_code && !companyCodes.find(c => c.company_code_id === org.company_code) && (
                        <option value={org.company_code}>{org.company_code}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <Label>Personnel Area</Label>
                    <select
                      value={org.personnel_area || ''}
                      onChange={(e) => handleOrgChange('personnel_area', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="">Select Personnel Area</option>
                      {personnelAreas.map(pa => (
                        <option key={pa.personnel_area_id} value={pa.personnel_area_id}>
                          {pa.personnel_area_id} - {pa.name}
                        </option>
                      ))}
                      {org.personnel_area && !personnelAreas.find(p => p.personnel_area_id === org.personnel_area) && (
                        <option value={org.personnel_area}>{org.personnel_area}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <Label>Personnel Subarea</Label>
                    <select
                      value={org.personnel_subarea || ''}
                      onChange={(e) => handleOrgChange('personnel_subarea', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="">Select Personnel Subarea</option>
                      {personnelSubareas
                        .filter(psa => !org.personnel_area || psa.personnel_area_id === org.personnel_area)
                        .map(psa => (
                          <option key={`${psa.personnel_area_id}-${psa.personnel_sub_area_id}`} value={psa.personnel_sub_area_id}>
                            {psa.personnel_sub_area_id} - {psa.name}
                          </option>
                        ))}
                      {org.personnel_subarea && !personnelSubareas.find(p => p.personnel_sub_area_id === org.personnel_subarea) && (
                        <option value={org.personnel_subarea}>{org.personnel_subarea}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <Label>Business Area</Label>
                    <Input
                      value={org.business_area || ''}
                      onChange={(e) => handleOrgChange('business_area', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Organizational Unit (Department)</Label>
                    <Input
                      value={org.organizational_unit || ''}
                      onChange={(e) => handleOrgChange('organizational_unit', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Position</Label>
                    <Input
                      value={org.position || ''}
                      onChange={(e) => handleOrgChange('position', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Job Title</Label>
                    <Input
                      value={org.job || ''}
                      onChange={(e) => handleOrgChange('job', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Cost Center</Label>
                    <select
                      value={org.cost_center || ''}
                      onChange={(e) => handleOrgChange('cost_center', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="">Select Cost Center</option>
                      {costCenters.map(cc => (
                        <option key={cc.cost_center_id} value={cc.cost_center_id}>
                          {cc.cost_center_id} - {cc.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Employee Group</Label>
                    <select
                      value={org.employee_group || ''}
                      onChange={(e) => handleOrgChange('employee_group', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="">Select Employee Group</option>
                      {employeeGroups.map(eg => (
                        <option key={eg.employee_group_id} value={eg.employee_group_id}>
                          {eg.employee_group_id} - {eg.name}
                        </option>
                      ))}
                      {org.employee_group && !employeeGroups.find(e => e.employee_group_id === org.employee_group) && (
                        <option value={org.employee_group}>{org.employee_group}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <Label>Employee Subgroup</Label>
                    <select
                      value={org.employee_subgroup || ''}
                      onChange={(e) => handleOrgChange('employee_subgroup', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="">Select Employee Subgroup</option>
                      {employeeSubgroups
                        .filter(esg => !org.employee_group || esg.employee_group_id === org.employee_group)
                        .map(esg => (
                          <option key={`${esg.employee_group_id}-${esg.employee_subgroup_id}`} value={esg.employee_subgroup_id}>
                            {esg.employee_subgroup_id} - {esg.name}
                          </option>
                        ))}
                      {org.employee_subgroup && !employeeSubgroups.find(e => e.employee_subgroup_id === org.employee_subgroup) && (
                        <option value={org.employee_subgroup}>{org.employee_subgroup}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <Label>Payroll Area</Label>
                    <Input
                      value={org.payroll_area || ''}
                      onChange={(e) => handleOrgChange('payroll_area', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Supervisor ID</Label>
                    <Input
                      value={org.supervisor_id || ''}
                      onChange={(e) => handleOrgChange('supervisor_id', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employment Tab */}
          <TabsContent value="employment">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-stone-700" />
                  Employment Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Hire Date</Label>
                    <Input
                      type="date"
                      value={emp.hire_date || ''}
                      onChange={(e) => handleEmploymentChange('hire_date', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Employment Type</Label>
                    <select
                      value={emp.employment_type || ''}
                      onChange={(e) => handleEmploymentChange('employment_type', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="FT">Full Time</option>
                      <option value="PT">Part Time</option>
                      <option value="CT">Contract</option>
                      <option value="IN">Intern</option>
                      <option value="TM">Temporary</option>
                    </select>
                  </div>
                  <div>
                    <Label>Probation End Date</Label>
                    <Input
                      type="date"
                      value={emp.probation_end_date || ''}
                      onChange={(e) => handleEmploymentChange('probation_end_date', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Confirmation Date</Label>
                    <Input
                      type="date"
                      value={emp.confirmation_date || ''}
                      onChange={(e) => handleEmploymentChange('confirmation_date', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Notice Period (Days)</Label>
                    <Input
                      type="number"
                      value={emp.notice_period_days || ''}
                      onChange={(e) => handleEmploymentChange('notice_period_days', parseInt(e.target.value) || 0)}
                      disabled={!isEditMode}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Work Schedule</Label>
                    <Input
                      value={emp.work_schedule || ''}
                      onChange={(e) => handleEmploymentChange('work_schedule', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <select
                      value={formData.status || 'ACTIVE'}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="ON_LEAVE">On Leave</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="TERMINATED">Terminated</option>
                      <option value="RETIRED">Retired</option>
                    </select>
                  </div>
                  <div>
                    <Label>Termination Date</Label>
                    <Input
                      type="date"
                      value={emp.termination_date || ''}
                      onChange={(e) => handleEmploymentChange('termination_date', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Communication Tab */}
          <TabsContent value="communication">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-stone-700" />
                  Communication (Infotype 0105)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Work Email</Label>
                    <Input
                      type="email"
                      value={comm.work_email || ''}
                      onChange={(e) => handleCommChange('work_email', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Personal Email</Label>
                    <Input
                      type="email"
                      value={comm.personal_email || ''}
                      onChange={(e) => handleCommChange('personal_email', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Work Phone</Label>
                    <Input
                      value={comm.work_phone || ''}
                      onChange={(e) => handleCommChange('work_phone', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Mobile Phone</Label>
                    <Input
                      value={comm.mobile_phone || ''}
                      onChange={(e) => handleCommChange('mobile_phone', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium mb-3">Emergency Contact</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Contact Name</Label>
                        <Input
                          value={comm.emergency_contact_name || ''}
                          onChange={(e) => handleCommChange('emergency_contact_name', e.target.value)}
                          disabled={!isEditMode}
                        />
                      </div>
                      <div>
                        <Label>Contact Phone</Label>
                        <Input
                          value={comm.emergency_contact_phone || ''}
                          onChange={(e) => handleCommChange('emergency_contact_phone', e.target.value)}
                          disabled={!isEditMode}
                        />
                      </div>
                      <div>
                        <Label>Relationship</Label>
                        <Input
                          value={comm.emergency_contact_relation || ''}
                          onChange={(e) => handleCommChange('emergency_contact_relation', e.target.value)}
                          disabled={!isEditMode}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Address Tab */}
          <TabsContent value="address">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-stone-700" />
                  Address Information (Infotype 0006)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Address Type</Label>
                    <select
                      value={address.address_type || 'CURRENT'}
                      onChange={(e) => handleAddressChange(0, 'address_type', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="CURRENT">Current Address</option>
                      <option value="PERMANENT">Permanent Address</option>
                      <option value="EMERGENCY">Emergency Address</option>
                    </select>
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input
                      value={address.country || ''}
                      onChange={(e) => handleAddressChange(0, 'country', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Address Line 1</Label>
                    <Input
                      value={address.address_line1 || ''}
                      onChange={(e) => handleAddressChange(0, 'address_line1', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Address Line 2</Label>
                    <Input
                      value={address.address_line2 || ''}
                      onChange={(e) => handleAddressChange(0, 'address_line2', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input
                      value={address.city || ''}
                      onChange={(e) => handleAddressChange(0, 'city', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={address.state || ''}
                      onChange={(e) => handleAddressChange(0, 'state', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input
                      value={address.postal_code || ''}
                      onChange={(e) => handleAddressChange(0, 'postal_code', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={address.phone || ''}
                      onChange={(e) => handleAddressChange(0, 'phone', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bank Tab */}
          <TabsContent value="bank">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-stone-700" />
                  Bank Details (Infotype 0009)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={bank.bank_name || ''}
                      onChange={(e) => handleBankChange(0, 'bank_name', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Branch</Label>
                    <Input
                      value={bank.bank_branch || ''}
                      onChange={(e) => handleBankChange(0, 'bank_branch', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={bank.account_number || ''}
                      onChange={(e) => handleBankChange(0, 'account_number', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Account Holder Name</Label>
                    <Input
                      value={bank.account_holder_name || ''}
                      onChange={(e) => handleBankChange(0, 'account_holder_name', e.target.value)}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      value={bank.ifsc_code || ''}
                      onChange={(e) => handleBankChange(0, 'ifsc_code', e.target.value.toUpperCase())}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>SWIFT Code</Label>
                    <Input
                      value={bank.swift_code || ''}
                      onChange={(e) => handleBankChange(0, 'swift_code', e.target.value.toUpperCase())}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div>
                    <Label>Account Type</Label>
                    <select
                      value={bank.account_type || ''}
                      onChange={(e) => handleBankChange(0, 'account_type', e.target.value)}
                      className="w-full h-10 px-3 border rounded-md disabled:bg-gray-100"
                      disabled={!isEditMode}
                    >
                      <option value="Savings">Savings</option>
                      <option value="Current">Current</option>
                      <option value="Salary">Salary</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Family Members Tab (Infotype 0021) */}
          <TabsContent value="family">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-stone-700" />
                  Family Members / Dependents (Infotype 0021)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(!formData.family_members || formData.family_members.length === 0) ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No family members recorded.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.family_members.map((member, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-gray-50">
                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Relationship</Label>
                            <p className="font-medium">{member.relationship || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Name</Label>
                            <p className="font-medium">{member.first_name} {member.last_name}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Date of Birth</Label>
                            <p className="font-medium">{member.date_of_birth || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Phone</Label>
                            <p className="font-medium">{member.phone_number || '-'}</p>
                          </div>
                          <div className="col-span-4 flex gap-4 text-sm">
                            {member.is_dependent && <Badge variant="outline">Dependent</Badge>}
                            {member.is_emergency_contact && <Badge variant="outline">Emergency Contact</Badge>}
                            {member.health_insurance_covered && <Badge variant="outline">Health Insurance</Badge>}
                            {member.life_insurance_beneficiary && <Badge variant="outline">Life Insurance Beneficiary</Badge>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recurring Payments/Deductions Tab (Infotype 0014) */}
          <TabsContent value="recurring">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-stone-700" />
                  Recurring Payments/Deductions (Infotype 0014)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(!formData.recurring_payments || formData.recurring_payments.length === 0) ? (
                  <div className="text-center py-8 text-gray-500">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No recurring payments or deductions recorded.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.recurring_payments.map((payment, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <Badge className={payment.payment_type === 'PAYMENT' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {payment.payment_type === 'PAYMENT' ? 'Payment' : 'Deduction'}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">{payment.currency} {payment.amount?.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">{payment.frequency}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Wage Type</Label>
                            <p className="font-medium">{payment.wage_type}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Description</Label>
                            <p className="font-medium">{payment.wage_type_name || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Start Date</Label>
                            <p className="font-medium">{payment.start_date}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">End Date</Label>
                            <p className="font-medium">{payment.end_date || 'Open'}</p>
                          </div>
                        </div>
                        {payment.remarks && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="text-gray-500">Remarks:</span> {payment.remarks}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Additional Payments Tab (Infotype 0015) */}
          <TabsContent value="additional">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-stone-700" />
                  Additional Payments (Infotype 0015)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(!formData.additional_payments || formData.additional_payments.length === 0) ? (
                  <div className="text-center py-8 text-gray-500">
                    <Plus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No additional payments recorded.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.additional_payments.map((payment, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <Badge className={payment.is_taxable ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}>
                              {payment.is_taxable ? 'Taxable' : 'Non-Taxable'}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">{payment.currency} {payment.amount?.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">{payment.payment_date}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Wage Type</Label>
                            <p className="font-medium">{payment.wage_type}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Description</Label>
                            <p className="font-medium">{payment.wage_type_name || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Payment Date</Label>
                            <p className="font-medium">{payment.payment_date}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Reason</Label>
                            <p className="font-medium">{payment.payment_reason || '-'}</p>
                          </div>
                        </div>
                        {payment.remarks && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="text-gray-500">Remarks:</span> {payment.remarks}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Remarks */}
        <Card>
          <CardContent className="pt-6">
            <Label>Additional Remarks</Label>
            <Textarea
              value={formData.remarks || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder="Any additional notes about the employee..."
              rows={3}
              disabled={!isEditMode}
            />
          </CardContent>
        </Card>

        {/* Metadata */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4 text-sm text-gray-500">
              <div>
                <Label className="text-xs">Created At</Label>
                <p>{formData.created_at ? new Date(formData.created_at).toLocaleString() : '-'}</p>
              </div>
              <div>
                <Label className="text-xs">Updated At</Label>
                <p>{formData.updated_at ? new Date(formData.updated_at).toLocaleString() : 'Never'}</p>
              </div>
              <div>
                <Label className="text-xs">Created By</Label>
                <p>{formData.created_by || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="mt-6">
          <WorkflowActivity doctype="employee" recordId={employee_id} currentState={formData?.status} />
        </div>
      </div>
    </Layout>
  );
};

export default EmployeeView;
